import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "./App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Card from "./Card";
import SchafkopfGameSelect from "./GameSelect";
import CardSlot from "./CardSlot";
import PointsOverview from "./PointsOverview";
import NotificationContainer from "./NotificationContainer";

function getCard(stich, players, playerId, offset = 0) {
  return (
    stich.filter(
      (s) => s.player === players[(players.indexOf(playerId) + offset) % 4]
    )[0]?.card ?? ""
  );
}

function getSuperCard(stich, players, playerId, offset = 0) {
  return (
    stich.filter(
      (s) => s.player === players[(players.indexOf(playerId) + offset) % 4]
    )[0]?.super_card ?? ""
  );
}

function getSuperPlayer(stich, players, playerId, offset = 0) {
  return (
    stich.filter(
      (s) => s.player === players[(players.indexOf(playerId) + offset) % 4]
    )[0]?.super_player ?? ""
  );
}

function Game() {
  let { gameId, playerId } = useParams();
  const [hand, setHand] = useState([]);
  const [stich, setStich] = useState([]);
  const [lastStich, setLastStich] = useState([]);
  const [wonStiche, setWonStiche] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState("");
  const [players, setPlayers] = useState([]);
  const [gameChoice, setGameChoice] = useState({});
  const [finalGameChoice, setFinalGameChoice] = useState("");
  const [finalPlayerChoice, setFinalPlayerChoice] = useState("");
  const [selectedGame, setSelectedGame] = useState("");
  const [data, setData] = useState({});
  const [pointsData, setPointsData] = useState({});
  const [isQuanten, setisQuanten] = useState(false);

  // Notifications for Tutorial
  const [currentNotifIndex, setCurrentNotifIndex] = useState(0); 
  const [hideNotifications, setHideNotifications] = useState(false); 
  const [lastSeenNotifIndex, setlastSeenNotifIndex] = useState(0); 
  const [notifications, setNotifications] = useState([]);

  const [winner, setWinner] = useState("");

  // Quanten logic
  const [currentSuperoffset, setCurrentSuperoffset] = useState(null);
  const [currentSuperplayerId, setCurrentSuperPlayerId] = useState(null);
  const [currentSuperCard, setCurrentSupercard] = useState(null);

  const [superDecisions, setSuperDecisions] = useState([]);

  const [hoveredCard, setHoveredCard] = useState(null);

  const [pendingGameState, setPendingGameState] = useState(null);
  const isAnimating = useRef(false);
  const [superAnimKey, setSuperAnimKey] = useState(0);


  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  const ws = useRef(null);

  useEffect(() => {
    ws.current = new WebSocket(
      `${API_URL.replace("http", "ws")}/ws/game/${gameId}/${playerId}`
    );

    ws.current.onopen = () => console.log("ws opened");
    ws.current.onclose = () => {
      ws.current = new WebSocket(
        `${API_URL.replace("http", "ws")}/ws/game/${gameId}/${playerId}`
      );
    };

    const interval = setInterval(() => {
      if (ws.current.readyState === WebSocket.OPEN) {
        sendMessage({ type: "ping" });
      }
    }, 20000); // 20 seconds
  }, []);

  // Initialize or reuse the WebSocket connection
  useEffect(() => {
    if (!ws.current) return;

    ws.current.onopen = () => {
      setMessage("");
    };

    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case "game_state":
          if (isAnimating.current) {
            setPendingGameState(data);
            break;
          }

          setHand(data.hands[playerId] || []);
          setStich(data.stich);
          setCurrentPlayer(data.current_player);
          setPlayers(data.players);

          setFinalGameChoice(data.final_game_choice["game"]);

          setFinalPlayerChoice(data.final_game_choice["player_id"]);
          setGameChoice(data.game_choice);
          if (data.alle_stiche.length > 0)
            setLastStich(data.alle_stiche[data.alle_stiche.length - 1]);

          if (data.stich.length === 4 && !data.is_tutorial) {
            decideStich();
          }

          if (
            data.won_stiche &&
            data.won_stiche[playerId] &&
            data.stiche_punkte
          ) {
            setWonStiche(data.won_stiche[playerId]);
          } else {
            setWonStiche([]);
          }

          addNotifications(data.notifications);

          setData(data);
          setisQuanten(data.is_quanten);

          break;
        case "error":
          setMessage(data.message);
          break;
        case "winner":
          setWinner(data.winnerId);
          resetWinner();
          break;
        case "point_stats":
          setPointsData(data);
          break;

       case "super_decisions":
          isAnimating.current = true;
          setSuperDecisions(data.superDecisions);
          setSuperAnimKey(prev => prev + 1);
          setTimeout(() => {
            isAnimating.current = false;
            setPendingGameState(prev => {
              if (prev) {
                setHand(prev.hands[playerId] || []);
                setStich(prev.stich);
                setCurrentPlayer(prev.current_player);
              }
              return null;
            });
            setSuperDecisions([]);
          }, 8000);
          break;

        case "redirect":
          navigate(`/`);
          ws.current.close(); 
          break;
        case "ping":
          break;
        
        default:
          console.warn("Unknown WebSocket message type:", data.type);
      }
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setMessage("WebSocket connection error.");
    };

    ws.current.onclose = () => {
      ws.current = new WebSocket(
        `${API_URL.replace("http", "ws")}/ws/game/${gameId}/${playerId}`
      );
    };
  }, [gameId, playerId, finalGameChoice, data]);

  const sendMessage = (message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    } else {
      setMessage("WebSocket is not connected.");
    }
  };

  const playCard = async (card) => {
    setHoveredCard(null);

    if (currentSuperCard !== null && currentSuperCard !== card) {
      sendMessage({ type: "play_superposition", playerId, card, currentSuperCard, currentSuperplayerId });
      setCurrentSupercard(null);
      setCurrentSuperPlayerId(null);
      setCurrentSuperoffset(null);
      return;
    }

    if (currentSuperCard !== null && currentSuperCard === card){
      setMessage(`Du musst zwei verschiedene Karten für die Verschränkung wählen.`)
      return;
    }

    if (currentSuperplayerId !== null) {
      console.log(`Du hast ${card} für die Verschränkung gewählt.`);
      if (currentSuperCard === null) {
        setCurrentSupercard(card)
        return;
      }
    }

    sendMessage({ type: "play_card", playerId, card });
  };

  const decideStich = async () => {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    sendMessage({ type: "stich_nehmen", playerId });
  };

  const chooseGame = async (selectedGame) => {
    setSelectedGame(selectedGame);
    sendMessage({ type: "set_game_choice", selectedGame });
  };

  const chooseWantToPlay = async (wantsToPlay) => {
    sendMessage({ type: "set_wants_to_play", wantsToPlay });
  };

  const schiessen = async () => {
    sendMessage({ type: "schiessen" });
  };

  const getPointsData = async () => {
    sendMessage({ type: "getPointsData" });
  };

  const addNotifications = (newNotifications) => {
    let old_notifications = notifications.length;
    const list = Object.values(newNotifications || []);
    setNotifications(list);
    if (old_notifications !== list.length)
      setHideNotifications(false);
  };

  const currentNotif = notifications[currentNotifIndex]?.text ?? null;

  const handleNextNotification = () => {
    setCurrentNotifIndex(prev => prev + 1);
    sendMessage({ type: "user_read_notification" });
  };

  const clearNotification = () => {
    handleNextNotification();
    setHideNotifications(true);
  };

  useEffect(() => {
    if (hand.length <= 0 && stich.length <= 0) {
      getPointsData()
    }
  }, [hand, stich]);

  useEffect(() => {
    if (message !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  const hashCode = function (s) {
    var h = 0,
      l = s.length,
      i = 0;
    if (l > 0) while (i < l) h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
    return h;
  };

  const resetWinner = () => {
    setTimeout(() => setWinner(null), 2000);
  };

  const handleSuperPosition = (playerId, offset) => {
    if (currentSuperplayerId === playerId || currentSuperoffset == offset) {
      setCurrentSuperPlayerId(null);
      setCurrentSupercard(null);
      setCurrentSuperoffset(null);
      setHoveredCard(null);
    }
    else {
      setCurrentSuperPlayerId(playerId);
      setCurrentSuperoffset(offset);
    }
  };

  const slotMap = {};
  const availableSuperSlots = [];

  stich.forEach(play => {
    let owner;

    if(play.super_player && play.super_player !== -1){
      owner = play.super_player;
    }
    else {
      owner = play.player;
    }

    if (!(owner in slotMap)){
      slotMap[owner] = [];
    }

    if (play.super_card && play.card){
      slotMap[owner].push({
        superCard: play.card,
        card: play.super_card
      });
      availableSuperSlots.push(owner);
    } else {
      slotMap[owner].push({ 
        card: play.card
      });
    }
  });

  return (
    <div id="game">
      <div class="header">
        <h2>Schafkopf</h2>
      </div>

      {message && (
        <div class="error">
          <div className={"red"}>
            <FontAwesomeIcon icon="fa-solid fa-circle-xmark" />
            <p>{message}</p>
          </div>
        </div>
      )}

      
        {currentNotif && !hideNotifications && (
          <NotificationContainer
            toast={currentNotif}
            removeToast={handleNextNotification}
            closeToast={clearNotification}
            hasNext={currentNotifIndex < notifications.length - 1}
          />
        )}

      <div className={"flex-wrap"} class="players">
        {players.map((player, index) => (
          <div key={index} className={"large_text"}>
            <div
              className={
                player === currentPlayer && stich.length < 4 ? "green bold" : ""
              }
            >
              <img
                className={
                  player === currentPlayer && stich.length < 4 ? "active" : ""
                }
                src={`/profile/${Math.abs(hashCode(player) % 20)}.png`}
              />
              {player}
              {hand.length <= 0 && stich.length <= 0 && data.player && (
                <span
                  className={
                    data.player.includes(player) ? "punkte" : "punkte_alt"
                  }
                >
                  {data.stiche_punkte[player]}
                </span>
              )}
            </div>
          </div>
        ))}

        {!finalGameChoice && data.round === 0 && currentPlayer === playerId && (
          <div class="willstdu">
            <b>Willst du spielen?</b>
            <button class="accept" onClick={() => chooseWantToPlay(true)}>
              <FontAwesomeIcon icon="fa-solid fa-check" />
              Ich spiele.
            </button>
            <button class="decline" onClick={() => chooseWantToPlay(false)}>
              <FontAwesomeIcon icon="fa-solid fa-xmark" />
              Weiter.
            </button>
          </div>
        )}

        {finalGameChoice &&
          data.round === 1 &&
          stich.length <= 0 &&
          ((data.non_player.includes(playerId) &&
            data.game_multiplier === 1) ||
            (data.player.includes(playerId) &&
              data.game_multiplier === 2)) && (
            <div class="willstdu">
              <button class="accept" onClick={() => schiessen()}>
                <FontAwesomeIcon icon="fa-solid fa-check" />
                Schuss!
              </button>
            </div>
          )}

        {data.game_multiplier > 1 && (<span><FontAwesomeIcon icon="fa-solid fa-xmark" /> {data.game_multiplier}</span>)}


      </div>
     
      {!finalGameChoice &&
        data.round !== 0 &&
        data.wants_to_play &&
        data.wants_to_play[playerId] &&
        currentPlayer === playerId && (
          <SchafkopfGameSelect
            chooseGame={chooseGame}
          />
        )}
      {finalGameChoice && (
        <div class="aktuellesspiel">
          <span>
            <b>
              {finalPlayerChoice} spielt {finalGameChoice}!
            </b>
          </span>
        </div>
      )}
      

      {winner != null && winner?.length > 0 && (
        <div class="success">
          <div className="green">
            <p>{winner} gewinnt den Stich!</p>
          </div>
        </div>
      )}

      {finalGameChoice && finalGameChoice === "Auflage" && data.round >= 2 && finalPlayerChoice !== playerId && (
        <div
          style={{
            transform: "scale(0.4)", // Scale the card down to 50%
            transformOrigin: "top center", // Ensure the scaling happens from the top-left
            display: "inline-block",
          }}
        >
          <div style={{ display: "flex", gap: "10px", "flex-wrap": "wrap" }}>
            {data.hands[finalPlayerChoice].map((card, index) => (
              <div key={index}>
                <Card key={index} cardName={card} isTrump={card in data.trump_cards} />
              </div>
            ))}
          </div>
        </div>
      )}


      {data.recommendation && (
        <div>Spielvorschlag: {data.recommendation[playerId]}</div>
      )}

      {pointsData && hand.length <= 0 && stich.length <= 0 && (
        <PointsOverview pointsData={pointsData} finalGameChoice={finalGameChoice} gameMultiplier={data.game_multiplier} sendMessage={sendMessage} playerId={playerId} waitingForPlayers={data.waiting_for_players}>
        </PointsOverview>
      )}

      <div className={"handCardContainer"} >
        <div class="handcards">
          {hand.map((card, index) => (
            <div key={index} onClick={() => playCard(card)} onMouseEnter={() => setHoveredCard(card)} 
        onMouseLeave={() => setHoveredCard(null)}>
              <Card key={index} cardName={card} isTrump={card in data.trump_cards} highlight={currentSuperCard === card} />
            </div>
          ))}
        </div>
      </div>

      {!finalGameChoice && data.round <= 2 && data.wants_to_play && (
        <div class="chat">
          {Object.keys(data.wants_to_play).map((entry, index) => (
            <div class="chatbubble">
              <img
                className={`active`}
                src={`/profile/${Math.abs(hashCode(entry) % 20)}.png`}
              />
              <div>
                <b class="orange">{entry}:</b>{" "}
                {data.wants_to_play[entry] ? "Ich spiele." : "Weiter."}
                <b>{gameChoice[entry]}</b>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="schublade">
        <details>
          <summary>
            <b>Letzter Stich</b>
          </summary>
          {hand.length > 0 && (
            <div className={"smallCards"}>
              {lastStich.length > 0 ? (
                <>
                  <div className={"flex-wrap"}>
                    {lastStich.map((entry, index) => (
                      <div key={index}>
                        <Card key={index} cardName={entry.card} isTrump={entry.card in data.trump_cards} />
                        <p>(played by {entry.player})</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p>Keine gewonnene Stiche bisher</p>
              )}
            </div>
          )}
          {hand.length <= 0 && stich.length <= 0 && data.player && (
            <>
              <div>
                {wonStiche.length > 0 ? (
                  <>
                    <ul>
                      {wonStiche.map((stich, index) => (
                        <li key={index}>
                          {stich.map((card) => card.card).join(", ")}
                        </li>
                      ))}
                    </ul>
                    <div className={"bold"}>
                      Gesamtpunkte: {data.stiche_punkte[playerId]}{" "}
                    </div>
                  </>
                ) : (
                  <p>Keine gewonnen Stiche</p>
                )}
              </div>
              <div className={"bold"}>
                Dein Team (
                {data.player.includes(playerId)
                  ? data.player.join(", ")
                  : data.non_player.join(", ")}
                ) hat:{" "}
                {data.player.includes(playerId)
                  ? data.player.reduce(
                    (sum, player) => sum + data.stiche_punkte[player],
                    0
                  )
                  : data.non_player.reduce(
                    (sum, player) => sum + data.stiche_punkte[player],
                    0
                  )}{" "}
                Punkte
              </div>
            </>
          )}
        </details>
      </div>

      {(hand.length > 0 || stich.length > 0) && [0, 1, 2, 3].map((offset) => {
        const slotPlayer = players[(players.indexOf(playerId) + offset) % 4];
        const pairs = slotMap[slotPlayer] || [];

        const decision = superDecisions.find(d => d.owner === slotPlayer);


        return (
          <div key={offset} className={`absolute spieler${offset + 1}`}>
            <div className={`superposition ${offset === currentSuperoffset ? "clicked" : ""}`}>
              
              {isQuanten && (slotPlayer == playerId || availableSuperSlots.includes(slotPlayer)) && data.current_player == playerId && finalGameChoice && (
                <button onClick={() => handleSuperPosition(slotPlayer, offset)}>
                  Superposition
                </button>
              )}

              <CardSlot 
                  pairs={pairs} 
                  showSuperSlots={offset === currentSuperoffset} 
                  decidedSlot={decision?.chosen ?? null} 
                  animKey={superAnimKey} 
                  previewPair={offset === (currentSuperoffset ?? 0) ? {
                    card: currentSuperCard ?? hoveredCard,        
                    superCard: currentSuperCard ? hoveredCard : null  
                  } : null}
                  />
            </div>

            {finalGameChoice && (
              <div className={`absolute playerName`}>{slotPlayer}</div>
            )}
          </div>
        );
      })}
    </div>

  );
}

export default Game;
